import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import { OrganizationInfo } from "../../pages/Contacts/schema/contacts";
import { organizationApi } from "./organization.api";

interface PaginationMeta {
	page: number;
	limit: number;
	itemCount: number;
	pageCount: number;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
	/**
	 * `itemCount` is the server's count cap, not the real total — there are more rows than it
	 * reports. Render it via `formatCappedCount` so it reads "N+" (PREC-3953).
	 */
	isCapped?: boolean;
}

interface OrganizationState {
	organizations: OrganizationInfo[];
	organizationsMeta: PaginationMeta;
	competitors: OrganizationInfo[];
	competitorsMeta: PaginationMeta;
	/**
	 * Normalized company names resolved on demand, keyed by organization id. Bucketed by the id the
	 * request was made for rather than a single field, so a response that lands after the editor has
	 * moved to another lead cannot overwrite the one on screen.
	 */
	normalizedNamesById: Record<number, string>;
}

const defaultMeta: PaginationMeta = {
	page: 1,
	limit: 15,
	itemCount: 0,
	pageCount: 1,
	hasPreviousPage: false,
	hasNextPage: false,
};

const initialState: OrganizationState = {
	organizations: [],
	organizationsMeta: { ...defaultMeta },
	competitors: [],
	competitorsMeta: { ...defaultMeta },
	normalizedNamesById: {},
};

export const organizationSlice = createSlice({
	name: "organization",
	initialState,
	reducers: {
		clearOrganizations: (state) => {
			state.organizations = [];
		},
		clearCompetitors: (state) => {
			state.competitors = [];
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			organizationApi.endpoints.getOrganizations.matchFulfilled,
			(state, { payload }) => {
				if (!payload) {
					return;
				}

				if (payload.data) {
					state.organizations = payload.data;
				}

				if (payload.meta) {
					state.organizationsMeta = payload.meta;
				}
			}
		);
		builder.addMatcher(
			organizationApi.endpoints.getCompetitors.matchFulfilled,
			(state, { payload }) => {
				if (!payload) {
					return;
				}

				if (payload.data) {
					state.competitors = payload.data;
				}

				if (payload.meta) {
					state.competitorsMeta = payload.meta;
				}
			}
		);
		builder.addMatcher(
			organizationApi.endpoints.resolveOrganizationNormalizedName.matchFulfilled,
			(state, { payload, meta }) => {
				if (payload?.nameNormalized) {
					state.normalizedNamesById[meta.arg.originalArgs] = payload.nameNormalized;
				}
			}
		);
	},
});

export const { clearOrganizations, clearCompetitors } = organizationSlice.actions;
export const organizationReducer = organizationSlice.reducer;
export const organizationSelector = (state: RootState) => state[organizationSlice.name];
export const normalizedCompanyNamesSelector = (state: RootState): Record<number, string> =>
	state[organizationSlice.name].normalizedNamesById;
export const competitorSelector = (state: RootState) => ({
	competitors: state[organizationSlice.name].competitors,
	competitorsMeta: state[organizationSlice.name].competitorsMeta,
});
